import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useEffect, useState } from "react";

import { useThunkDispatch } from "@/redux/dispatcher";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import { load as loadCustomer } from "@/redux/modules/api/data/customer/customer.module";
import { addProductToRecentlyViewed } from "@/services/recentlyViewedProducts.service";

import { OnAddToCartHandler } from "../actionHandlers/onAddToCart.handler";
import { OnSubmitHandler } from "../actionHandlers/onSubmit.handler";
import { Tooltips } from "../eCommerce.types";

type ECommerceContextValue = {
    product: Product<ProductFieldSet.Full>;
    onSubmitHandler: OnSubmitHandler;
    onAddToCartHandler: OnAddToCartHandler;
    xfPath: string | null;
    tooltips: Tooltips;
    infoLink: string;
    cashbackPagePath: string;
    paintConfiguratorPath: string;
    isEcommerceReady: boolean;
    setInitialised: React.Dispatch<React.SetStateAction<boolean>>;
};

export type ECommerceContextProviderProps = {
    product: Product<ProductFieldSet.Full>;
    tooltips: Tooltips;
    infoLink: string;
    contextAwareXfPath: string | null;
    cashbackPagePath: string;
    paintConfiguratorPath: string;
    handlers: {
        onSubmit: OnSubmitHandler;
        onAddToCart: OnAddToCartHandler;
    };
};

export const ECommerceContext = createContext<
    ECommerceContextValue | undefined
>(undefined);

ECommerceContext.displayName = "ECommerceContext";

export const ECommerceContextProvider = ({
    children,
    product,
    tooltips,
    infoLink,
    contextAwareXfPath,
    cashbackPagePath,
    paintConfiguratorPath,
    handlers: { onSubmit: onSubmitHandler, onAddToCart: onAddToCartHandler }
}: RequireChildrenFor<ECommerceContextProviderProps>) => {
    const dispatch = useThunkDispatch();

    const [initialised, setInitialised] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        addProductToRecentlyViewed(product.sku);

        dispatch(CartModule.load());
        dispatch(loadCustomer());
    }, []);

    return (
        <ECommerceContext
            value={{
                product,
                onSubmitHandler,
                onAddToCartHandler,
                xfPath: contextAwareXfPath,
                tooltips,
                infoLink,
                cashbackPagePath,
                paintConfiguratorPath,
                isEcommerceReady: initialised,
                setInitialised
            }}
        >
            {children}
        </ECommerceContext>
    );
};

export const useECommerceContext = (): ECommerceContextValue => {
    const context = use<ECommerceContextValue | undefined>(ECommerceContext);
    if (!isDefined(context)) {
        throw new Error(`${ECommerceContext.displayName} is undefined.`);
    }
    return context;
};
